import { isAxiosError } from 'axios';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { orderBy } from 'es-toolkit';

import { instance } from '@/interfaces';
import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';
import { userInfoAtom } from '@/interfaces/member';
import { devicesAtom, getDevices } from '@/interfaces/devices';
import { siteAlarmHistoryPeriodAtom } from '@/components/alarm-history';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { APIResponse } from '@/@types/types';
import type { Device, Period } from '@/@types/device';
import type { AlarmHistory, AlarmSettingData, SiteAlarmHistoryData } from '@/@types/alarm';
import type { UserInfo } from '@/@types/user';

export const alarmSettingAtom = atomWithQuery((get) => ({
  queryKey: ['ALARM_SETTING', get(selectedDeviceSafeAtom)],
  queryFn: async ({ queryKey: [, selectedDeviceAtomValue] }) => {
    const device = selectedDeviceAtomValue as Device;
    const data = await getAlarmSetting(device.id);

    return data;
  },
}));
const defaultSetting: Omit<AlarmSettingData, 'id'> = {
  alarm: {
    temp: false,
    humi: false,
    battery: false,
    offline: false,
  },
  receiver: { email: [], sms: [] },
  threshold: {
    temp: { min: 2, max: 8 },
    humi: { min: 1, max: 70 },
    battery: 10,
    offline: 30,
  },
};
async function getAlarmSetting(sensorId: string) {
  try {
    const { data } = await instance.get<APIResponse<AlarmSettingData>>(`/alarm/sensor/${sensorId}`);
    return data.data ?? { id: sensorId, ...defaultSetting };
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[getGroupData] API error code:', err.response.data.errorCode);
    } else {
      console.error('[getGroupData] Unknown error', err);
    }
    return null;
  }
}

export const postAlarmSettingAtom = atomWithMutation((get) => ({
  mutationFn: async (setting: AlarmSettingData) => {
    const device = get(selectedDeviceSafeAtom);
    const isSuccess = await postAlarmSetting(device.id, setting);
    return isSuccess;
  },
}));
async function postAlarmSetting(sensorId: string, setting: AlarmSettingData) {
  try {
    const { data } = await instance.post<APIResponse>(`/alarm/sensor/${sensorId}`, setting);
    if (data.errorCode !== 'OK') {
      console.error('[postAlarmSetting] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[postAlarmSetting] API error code:', err.response.data.errorCode);
    } else {
      console.error('[postAlarmSetting] Unknown error', err);
    }
    return false;
  }
}

export const deviceAlarmHistoryAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['ALARM_HISTORY', get(userInfoAtom), get(selectedDeviceSafeAtom), get(deviceDataPeriodAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue, selectedDeviceAtomValue, deviceDataPeriodAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    const device = selectedDeviceAtomValue as Device;
    const { from, to } = deviceDataPeriodAtomValue as Period;
    const data = await getDeviceAlarmHistory(userInfo.site[0].id, device.id, from, to);

    return data;
  },
}));
async function getDeviceAlarmHistory(siteId: string, sensorId: string, from: Date, to: Date) {
  try {
    const { data } = await instance.get<APIResponse<AlarmHistory[]>>(`/alarm/site/${siteId}/history`, {
      params: { from, to, sensorId },
    });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[getDeviceAlarmHistory] API error code:', err.response.data.errorCode);
    } else {
      console.error('[getDeviceAlarmHistory] Unknown error', err);
    }
    return [];
  }
}

export const siteAlarmHistoryAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['ALARM_HISTORY', get(userInfoAtom), get(siteAlarmHistoryPeriodAtom), get(devicesAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue, siteAlarmHistoryPeriodAtomValue, deviceAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    let { data: devices } = deviceAtomValue as AtomWithQueryResult<Device[]>;
    const { from, to } = siteAlarmHistoryPeriodAtomValue as Period;

    if (devices === undefined) {
      devices = await getDevices(userInfo.site[0].id);
    }

    const data = await getSiteAlarmHistory(userInfo.site[0].id, from, to);
    const result: SiteAlarmHistoryData[] = data.map((alarm) => ({
      ...alarm,
      id: alarm.timestamp + alarm.alarmType + alarm.mac,
    }));
    return orderBy(result, ['timestamp', 'serialNumber'], ['desc', 'asc']);
  },
}));
async function getSiteAlarmHistory(siteId: string, from: Date, to: Date) {
  try {
    const { data } = await instance.get<APIResponse<AlarmHistory[]>>(`/alarm/site/${siteId}/history`, {
      params: { from, to },
    });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[getSiteAlarmHistory] API error code:', err.response.data.errorCode);
    } else {
      console.error('[getSiteAlarmHistory] Unknown error', err);
    }
    return [];
  }
}
